import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Phone, ArrowLeft, Smartphone } from 'lucide-react';
import { authService } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import { isValidPhone } from '../utils/phoneValidation';

/**
 * Connexion expéditeur (Particulier / Vendeur Social) — sans mot de passe.
 * Téléphone → code OTP à 4 chiffres reçu par SMS / WhatsApp (spéc. MAJ 06/09/26).
 */
export const LoginExpediteur: React.FC = () => {
  const navigate = useNavigate();
  const { setAuth, setLoginNotification } = useAuthStore();
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [telephone, setTelephone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  const cleanPhone = () => telephone.replace(/[\s.-]/g, '');

  const requestCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!isValidPhone(telephone)) return;
    try {
      setLoading(true);
      await authService.requestExpediteurOtp(cleanPhone());
      setStep('otp');
      toast.success('Si un compte existe, un code a été envoyé.');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    if (otp.length < 4) return;
    try {
      setLoading(true);
      const res = await authService.verifyExpediteurOtp(cleanPhone(), otp);
      setAuth(res.user as any, res.access_token!, res.refresh_token);
      setLoginNotification({ lastLoginAt: res.last_login_at ?? null, failedAttempts: 0 });
      toast.success('Connecté !');
      navigate('/mes-envois');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Code invalide');
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-500" />
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 shadow-2xl">
          <div className="flex flex-col items-center mb-6">
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mb-3">
              <Smartphone className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-xl font-bold text-white">Connexion par téléphone</h1>
            <p className="text-white/60 text-sm mt-1 text-center">
              Recevez un code par SMS ou WhatsApp — sans mot de passe
            </p>
          </div>

          {step === 'phone' ? (
            <form onSubmit={requestCode} className="space-y-4">
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60 pointer-events-none" />
                <input
                  type="tel"
                  autoFocus
                  placeholder="Téléphone (+237… ou international)"
                  className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-md"
                  value={telephone}
                  onChange={(e) => setTelephone(e.target.value)}
                />
              </div>
              <button
                type="submit"
                disabled={loading || !isValidPhone(telephone)}
                className="w-full py-3 bg-white text-indigo-700 font-semibold rounded-xl hover:bg-white/90 transition disabled:opacity-60"
              >
                {loading ? 'Envoi...' : 'Recevoir mon code'}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <p className="text-white/80 text-sm text-center">
                Code à 4 chiffres envoyé au <strong>{telephone}</strong>.
              </p>
              <input
                type="text"
                inputMode="numeric"
                maxLength={4}
                autoFocus
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="0000"
                className="w-full text-center tracking-[0.5em] text-2xl py-3 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
              <button
                onClick={verify}
                disabled={otp.length < 4 || loading}
                className="w-full py-3 bg-white text-indigo-700 font-semibold rounded-xl hover:bg-white/90 transition disabled:opacity-60"
              >
                {loading ? 'Vérification...' : 'Se connecter'}
              </button>
              <button onClick={() => requestCode()} disabled={loading} className="w-full text-white/70 text-sm hover:text-white transition">
                Code non reçu ? Renvoyer
              </button>
            </div>
          )}

          <div className="flex items-center justify-between mt-6 text-sm">
            <Link to="/login" className="text-white/70 hover:text-white flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Mot de passe
            </Link>
            <Link to="/register-expediteur" className="text-white font-medium hover:underline">
              Créer un compte
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
